import { MediaImage } from "@/components/ui/MediaImage";
import type { MediaItem } from "@/data/media";
import { useMedia } from "@/content-context";

/**
 * Documentary image with an explicit aspect ratio (so nothing shifts as it loads)
 * and an optional caption. The frame crops; the image is never stretched, and
 * the focal point keeps the subject in view whatever shape the frame is.
 *
 * Goes through MediaImage rather than straight to the build-time registry, so a
 * photograph uploaded in the panel renders here exactly as a committed one does.
 */
export function MediaFigure({
  item: source,
  ratio = "4 / 3",
  sizes = "(min-width: 1024px) 50vw, 100vw",
  priority = false,
  showCaption = true,
  radius = 20,
  className = "",
  objectPosition,
}: {
  item: MediaItem;
  ratio?: string;
  sizes?: string;
  priority?: boolean;
  showCaption?: boolean;
  radius?: number;
  className?: string;
  /** A crop chosen for this placement, which wins over the photograph's own. */
  objectPosition?: string;
}) {
  const item = useMedia()(source);
  // See MediaImage: a missing public CMS overlay may mean the API withheld an
  // unconsented photograph, not that the committed fallback should be shown.
  if (item.needsConsentCheck && item.consentStatus !== "granted") return null;
  return (
    <figure className={className}>
      <div
        className="relative w-full overflow-hidden bg-mist"
        style={{ aspectRatio: ratio, borderRadius: `${radius}px` }}
      >
        <MediaImage
          item={item}
          fill
          sizes={sizes}
          priority={priority}
          className="object-cover"
          objectPosition={objectPosition || item.objectPosition || "50% 50%"}
        />
      </div>
      {showCaption ? (
        <figcaption className="mt-4 text-[0.875rem] leading-relaxed text-muted">
          {item.caption}
          {item.location || item.year ? (
            <span className="mt-1 block text-ink/60">
              {[item.location, item.year].filter(Boolean).join(", ")}
            </span>
          ) : null}
          {item.credit ? <span className="block text-ink/55">Photo: {item.credit}</span> : null}
        </figcaption>
      ) : null}
    </figure>
  );
}
